import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import {
  Button,
  Typography,
  IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  SignalWifiOff,
  PhonelinkErase,
  Block,
  Close,
  ArrowForward,
} from "@material-ui/icons";
import { AuthContext } from "../../context/Auth/AuthContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import alertSound from "../../assets/chat_notify.mp3";

const useStyles = makeStyles((theme) => ({
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    backdropFilter: "blur(4px)",
    padding: 16,
  },
  card: {
    width: "100%",
    maxWidth: 460,
    borderRadius: 16,
    overflow: "hidden",
    backgroundColor: theme.palette.type === "dark" ? "#1e1e1e" : "#fff",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
    animation: "$slideIn 0.2s ease-out",
  },
  "@keyframes slideIn": {
    "0%": { opacity: 0, transform: "scale(0.95)" },
    "100%": { opacity: 1, transform: "scale(1)" },
  },
  topBar: {
    height: 4,
    background: "linear-gradient(90deg, #d32f2f, #ff9800, #d32f2f)",
  },
  header: {
    padding: "20px 24px 16px",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  iconBox: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(211, 47, 47, 0.1)",
    border: "1px solid rgba(211, 47, 47, 0.2)",
  },
  pulseIcon: {
    color: "#d32f2f",
    fontSize: 22,
    animation: "$pulse 1.5s infinite",
  },
  "@keyframes pulse": {
    "0%": { opacity: 1 },
    "50%": { opacity: 0.5 },
    "100%": { opacity: 1 },
  },
  title: {
    fontSize: 17,
    fontWeight: 600,
    color: theme.palette.text.primary,
    lineHeight: 1.3,
  },
  subtitle: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  alertList: {
    padding: "0 24px 16px",
    maxHeight: 300,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  alertItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    padding: "12px 14px",
    borderRadius: 12,
    border: `1px solid`,
  },
  alertItemLoggedOut: {
    backgroundColor: "rgba(211, 47, 47, 0.06)",
    borderColor: "rgba(211, 47, 47, 0.2)",
  },
  alertItemDisconnected: {
    backgroundColor: "rgba(211, 47, 47, 0.06)",
    borderColor: "rgba(211, 47, 47, 0.2)",
  },
  alertItemReconnecting: {
    backgroundColor: "rgba(255, 152, 0, 0.06)",
    borderColor: "rgba(255, 152, 0, 0.2)",
  },
  alertItemBanned: {
    backgroundColor: "rgba(136, 14, 79, 0.06)",
    borderColor: "rgba(136, 14, 79, 0.2)",
  },
  alertIconBox: {
    width: 32,
    height: 32,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)",
    flexShrink: 0,
    marginTop: 2,
  },
  alertContent: {
    flex: 1,
    minWidth: 0,
  },
  alertName: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.text.primary,
    lineHeight: 1.3,
  },
  alertMessage: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginTop: 4,
    lineHeight: 1.5,
  },
  actions: {
    padding: "0 24px 20px",
    display: "flex",
    gap: 10,
  },
  dismissBtn: {
    flex: 1,
    textTransform: "none",
    borderRadius: 10,
    padding: "10px 16px",
    fontSize: 13,
    fontWeight: 500,
  },
  connectionsBtn: {
    flex: 1,
    textTransform: "none",
    borderRadius: 10,
    padding: "10px 16px",
    fontSize: 13,
    fontWeight: 600,
    backgroundColor: "#1976d2",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#1565c0",
    },
  },
}));

const ALERT_CONFIG = {
  logged_out: {
    icon: PhonelinkErase,
    color: "#d32f2f",
    itemClass: "alertItemLoggedOut",
  },
  reconnect_failed: {
    icon: SignalWifiOff,
    color: "#e65100",
    itemClass: "alertItemReconnecting",
  },
  session_banned: {
    icon: Block,
    color: "#880e4f",
    itemClass: "alertItemBanned",
  },
  disconnected: {
    icon: SignalWifiOff,
    color: "#d32f2f",
    itemClass: "alertItemDisconnected",
  },
};

const GlobalConnectionAlert = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const { whatsApps } = useContext(WhatsAppsContext);
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);
  const audioRef = useRef(null);
  const prevStatusRef = useRef({});
  const manualDisconnectsRef = useRef(new Set());

  const playAlertSound = useCallback(() => {
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      audioRef.current = new Audio(alertSound);
      audioRef.current.volume = 0.8;
      audioRef.current.play().catch(() => {});
    } catch (e) {}
  }, []);

  const addAlert = useCallback((data) => {
    const newAlert = {
      id: `${data.whatsappId}-${Date.now()}`,
      whatsappId: data.whatsappId,
      whatsappName: data.whatsappName,
      action: data.action,
      message: data.message,
      retryCount: data.retryCount || 0,
      maxRetries: data.maxRetries || 5,
      timestamp: data.timestamp || new Date().toISOString(),
    };

    setAlerts((prev) => {
      const filtered = prev.filter(
        (a) => a.whatsappId !== data.whatsappId
      );
      return [newAlert, ...filtered];
    });

    setOpen(true);
    playAlertSound();
  }, [playAlertSound]);

  // MÉTODO 1: Escutar evento direto do backend (connectionAlert)
  useEffect(() => {
    if (!socket || !user?.companyId) return;

    const companyId = user.companyId;

    const onConnectionAlert = (data) => {
      if (!data || !data.action) return;

      if (data.action === "connected") {
        setAlerts((prev) => {
          const filtered = prev.filter((a) => a.whatsappId !== data.whatsappId);
          if (filtered.length === 0) setOpen(false);
          return filtered;
        });
        return;
      }

      addAlert(data);
    };

    socket.on(`company-${companyId}-connectionAlert`, onConnectionAlert);

    return () => {
      socket.off(`company-${companyId}-connectionAlert`, onConnectionAlert);
    };
  }, [socket, user?.companyId, addAlert]);

  // MÉTODO 2: Monitorar mudanças de status via WhatsAppsContext
  useEffect(() => {
    if (!whatsApps || whatsApps.length === 0) return;

    const currentStatus = {};
    whatsApps.forEach((w) => {
      currentStatus[w.id] = w.status;
    });

    const prevStatus = prevStatusRef.current;
    const prevKeys = Object.keys(prevStatus);

    if (prevKeys.length > 0) {
      whatsApps.forEach((w) => {
        const prev = prevStatus[w.id];
        const curr = w.status;

        if (prev !== curr) {
          if (prev === "CONNECTED" && curr === "DISCONNECTED") {
            if (manualDisconnectsRef.current.has(w.id)) {
              manualDisconnectsRef.current.delete(w.id);
              return;
            }

            setAlerts((prevAlerts) => {
              const alreadyHasAlert = prevAlerts.some(
                (a) => a.whatsappId === w.id && (Date.now() - new Date(a.timestamp).getTime()) < 15000
              );
              if (alreadyHasAlert) return prevAlerts;

              const newAlert = {
                id: `${w.id}-${Date.now()}`,
                whatsappId: w.id,
                whatsappName: w.name,
                action: "disconnected",
                message: `A conexão "${w.name}" foi desconectada. Verifique o celular ou escaneie um novo QR Code.`,
                timestamp: new Date().toISOString(),
              };

              setOpen(true);
              playAlertSound();
              return [newAlert, ...prevAlerts.filter((a) => a.whatsappId !== w.id)];
            });
          }

          if (prev !== "CONNECTED" && curr === "CONNECTED") {
            setAlerts((prevAlerts) => {
              const filtered = prevAlerts.filter((a) => a.whatsappId !== w.id);
              if (filtered.length === 0) setOpen(false);
              return filtered;
            });
          }
        }
      });
    }

    prevStatusRef.current = currentStatus;
  }, [whatsApps, playAlertSound]);

  // Escutar desconexões manuais
  useEffect(() => {
    const handleManualDisconnect = (e) => {
      if (e.detail?.whatsappId) {
        manualDisconnectsRef.current.add(e.detail.whatsappId);
      }
    };

    window.addEventListener("manual-disconnect", handleManualDisconnect);
    return () => {
      window.removeEventListener("manual-whatsapp-disconnect", handleManualDisconnect);
    };
  }, []);

  const handleDismissAll = () => {
    setAlerts([]);
    setOpen(false);
  };

  const handleGoToConnections = () => {
    setAlerts([]);
    setOpen(false);
    history.push("/connections");
  };

  const formatTime = (timestamp) => {
    try {
      return new Date(timestamp).toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    } catch {
      return "";
    }
  };

  if (alerts.length === 0 || !open) return null;

  return (
    <div className={classes.overlay}>
      <div className={classes.card}>
        <div className={classes.topBar} />

        <div className={classes.header}>
          <div className={classes.headerLeft}>
            <div className={classes.iconBox}>
              <SignalWifiOff className={classes.pulseIcon} />
            </div>
            <div>
              <Typography className={classes.title}>
                {alerts.length === 1
                  ? "Conexão desconectada"
                  : `${alerts.length} conexões desconectadas`}
              </Typography>
              <Typography className={classes.subtitle}>
                Ação necessária
              </Typography>
            </div>
          </div>
          <IconButton
            size="small"
            onClick={() => setOpen(false)}
            style={{ marginTop: -4, marginRight: -8 }}
          >
            <Close fontSize="small" />
          </IconButton>
        </div>

        <div className={classes.alertList}>
          {alerts.map((alert) => {
            const config = ALERT_CONFIG[alert.action] || ALERT_CONFIG.disconnected;
            const AlertIcon = config.icon;
            const itemClassName = classes[config.itemClass] || classes.alertItemDisconnected;

            return (
              <div
                key={alert.id}
                className={`${classes.alertItem} ${itemClassName}`}
              >
                <div className={classes.alertIconBox}>
                  <AlertIcon style={{ color: config.color, fontSize: 18 }} />
                </div>
                <div className={classes.alertContent}>
                  <Typography className={classes.alertName}>
                    {alert.whatsappName}
                  </Typography>
                  <Typography className={classes.alertMessage}>
                    {alert.message}
                  </Typography>
                  <Typography
                    style={{ fontSize: 11, color: "#999", marginTop: 4 }}
                  >
                    {formatTime(alert.timestamp)}
                  </Typography>
                </div>
              </div>
            );
          })}
        </div>

        <div className={classes.actions}>
          <Button
            variant="outlined"
            className={classes.dismissBtn}
            onClick={handleDismissAll}
          >
            Dispensar
          </Button>
          <Button
            className={classes.connectionsBtn}
            onClick={handleGoToConnections}
            endIcon={<ArrowForward style={{ fontSize: 16 }} />}
          >
            Ver conexões
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GlobalConnectionAlert;
